import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import userStatusService from '../services/userStatusService';
import socketService from '../services/unifiedSocketService';
import { logger } from '../utils/logger';

/**
 * Hook pour maintenir le statut "en ligne" de l'utilisateur
 * Met à jour updated_at régulièrement pour indiquer que l'utilisateur est actif
 * Fonctionne pour tous les rôles (PATIENT, DOCTOR, ADMIN)
 */
export function useAutoOnlineStatus() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!user || loading) return;

    let cancelled = false;
    let heartbeatInterval: NodeJS.Timeout | null = null;

    const sendHeartbeat = async (source: string) => {
      if (cancelled) return;
      
      // 🛡️ Vérification préventive : ne pas envoyer si token supprimé
      if (!authService.isAuthenticated()) {
        logger.debug(`⚠️ [useAutoOnlineStatus] Session expirée, arrêt du heartbeat (${source})`);
        if (heartbeatInterval) clearInterval(heartbeatInterval);
        cancelled = true;
        return;
      }

      try {
        await userStatusService.sendHeartbeat(window.location.pathname);
        logger.debug(`✅ [useAutoOnlineStatus] Heartbeat (${source}) envoyé pour ${user.role}`);
      } catch (err: any) {
        logger.warn(`⚠️ [useAutoOnlineStatus] Échec heartbeat (${source})`, err);
        
        // Si c'est une erreur 401 ou que la session est invalidée, on arrête la boucle
        if (err?.statusCode === 401 || err?.message?.includes('401')) {
           logger.debug(`⚠️ [useAutoOnlineStatus] Erreur 401 détectée, arrêt définitif de l'intervalle.`);
           if (heartbeatInterval) clearInterval(heartbeatInterval);
           cancelled = true;
        }
      }
    };

    // 1. Démarrage initial
    void (async () => {
      await userStatusService.ensurePresenceConfig();
      await sendHeartbeat('initial');
      
      // 2. Configurer l'intervalle régulier (toutes les 30 secondes)
      // On utilise une valeur inférieure au TTL Redis (souvent 60s ou plus)
      if (!cancelled) {
        heartbeatInterval = setInterval(() => {
          void sendHeartbeat('interval');
        }, 30000);
      }
    })();

    // 3. Marquer comme en ligne quand l'onglet reprend le focus
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void sendHeartbeat('visibility');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleSocketReconnect = () => {
      void sendHeartbeat('socket_reconnect');
    };

    socketService.onConnected(handleSocketReconnect);

    return () => {
      cancelled = true;
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      socketService.offConnected(handleSocketReconnect);
    };
  }, [user, loading]);
}
